import { FiMic } from 'react-icons/fi';

export const fluencyTrainingData = {
  courseId: 'fluency-training',
  courseType: 'speaking-course',
  title: 'Fluency Training',
  icon: FiMic,
  overview: {
    impact: 'Speak naturally without hesitation',
    focus: 'Maintaining flow and reducing pauses',
    uses: 'All Speaking parts',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'What Is Fluency?',
      content: `<p>Fluency is the ability to speak smoothly without unnatural pauses. In IELTS, fluency is assessed on:</p>`,
      points: [
        'Speaking at a natural pace',
        'Avoiding long, awkward pauses',
        'Self-correcting without losing flow',
        'Speaking coherently with logical connections',
      ],
      warning: 'Fluency ≠ Speed. Speaking too fast can hurt clarity. Aim for natural pace.',
    },
    {
      title: 'Common Fluency Blockers',
      content: `<p>Identify and overcome these common issues:</p>`,
      table: {
        headers: ['Problem', 'Cause', 'Solution'],
        rows: [
          ['Long pauses', 'Thinking in L1', 'Use fillers; think in English'],
          ['Repetition', 'Searching for words', 'Paraphrase; use simpler words'],
          ['False starts', 'Changing ideas mid-sentence', 'Plan before speaking'],
          ['Over-correction', 'Perfectionism', 'Accept minor errors; keep going'],
        ],
      },
    },
    {
      title: 'Natural Fillers',
      content: `<p>Use these to buy thinking time without awkward silence:</p>`,
      points: [
        '"Well..." — to introduce a thought',
        '"Let me think..." — when you need a moment',
        '"Actually..." — to add information',
        '"I mean..." — to clarify or rephrase',
        '"You know..." — to connect with listener',
        '"To be honest..." — to introduce an opinion',
      ],
      warning: 'Don\'t overuse fillers! They should be occasional, not constant.',
    },
    {
      title: 'Fluency Techniques',
      content: `<p>Practice these techniques to improve flow:</p>`,
      points: [
        '<strong>Chunking:</strong> Speak in phrase groups, not word by word',
        '<strong>Linking:</strong> Connect words smoothly (e.g., "I am" → "I\'m")',
        '<strong>Paraphrasing:</strong> If stuck, say it differently',
        '<strong>Extending:</strong> Add details to fill time naturally',
        '<strong>Thinking aloud:</strong> "That\'s an interesting question because..."',
      ],
      examples: [
        { label: 'Chunking', text: '"I think | technology is important | because it helps us | communicate faster."' },
        { label: 'Extending', text: '"I like reading... especially fiction novels... things like mystery or thriller genres."' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Use Appropriate Fillers',
      type: 'select',
      instructions: 'Choose the best filler for each situation.',
      questions: [
        {
          text: 'You need a moment to think about your answer.',
          options: ['"Let me think..."', '"Actually..."', '"I mean..."'],
          answer: '"Let me think..."',
          explanation: 'This signals you\'re considering the question and buying time.',
        },
        {
          text: 'You want to add something to what you just said.',
          options: ['"Actually..."', '"Well..."', '"Let me think..."'],
          answer: '"Actually..."',
          explanation: '"Actually" adds or modifies previous information.',
        },
        {
          text: 'You want to clarify what you meant.',
          options: ['"I mean..."', '"Well..."', '"To be honest..."'],
          answer: '"I mean..."',
          explanation: '"I mean" is used to rephrase or clarify your point.',
        },
        {
          text: 'You want to give your honest opinion.',
          options: ['"To be honest..."', '"Let me think..."', '"Actually..."'],
          answer: '"To be honest..."',
          explanation: 'This phrase introduces a sincere personal opinion.',
        },
        {
          text: 'You want to start explaining a new idea.',
          options: ['"Well..."', '"I mean..."', '"You know..."'],
          answer: '"Well..."',
          explanation: '"Well" naturally introduces a new thought or explanation.',
        },
      ],
    },
    {
      title: 'Identify Fluency Problems',
      type: 'select',
      instructions: 'What fluency issue does each example show?',
      questions: [
        {
          text: '"I like... um... I like... the thing is... I like..."',
          options: ['Repetition/False starts', 'Speaking too fast', 'Over-correction'],
          answer: 'Repetition/False starts',
          explanation: 'The speaker keeps starting over instead of completing thoughts.',
        },
        {
          text: '"I went to shop—no, the store—actually it was the supermarket—I mean the grocery store..."',
          options: ['Over-correction', 'Long pauses', 'Speaking too fast'],
          answer: 'Over-correction',
          explanation: 'The speaker is correcting minor details too much instead of continuing.',
        },
        {
          text: '(10-second silence before answering)',
          options: ['Long pauses', 'Repetition', 'Over-correction'],
          answer: 'Long pauses',
          explanation: 'Use fillers like "Let me think" to avoid awkward silence.',
        },
      ],
    },
    {
      title: 'Chunking Practice',
      type: 'select',
      instructions: 'Which sentence shows good chunking (natural phrase groups)?',
      questions: [
        {
          text: 'Which is chunked naturally?',
          options: ['I think | technology is important | because it helps us | communicate.', 'I | think | technology | is | important | because...'],
          answer: 'I think | technology is important | because it helps us | communicate.',
          explanation: 'Good chunking groups words into meaningful phrases, not individual words.',
        },
        {
          text: 'Which sounds more natural?',
          options: ['In my opinion, | working from home | has several advantages.', 'In | my | opinion, | working | from | home...'],
          answer: 'In my opinion, | working from home | has several advantages.',
          explanation: 'Speak in phrase groups, pausing briefly between chunks.',
        },
      ],
    },
    {
      title: 'Extend Your Answers',
      type: 'writing',
      instructions: 'Extend these short answers using details, examples, and reasons.',
      questions: [
        {
          text: 'Short answer: "I like music."',
          example: 'I like music, especially pop and R&B. I usually listen when I\'m commuting or working out, and I find it really helps me relax after a long day.',
          tip: 'Add: What type? When do you listen? How does it make you feel?',
        },
        {
          text: 'Short answer: "My hometown is nice."',
          example: 'My hometown is quite nice, actually. It\'s a coastal city with beautiful beaches, and the weather is pleasant most of the year. What I particularly like is the food—we have amazing seafood restaurants.',
          tip: 'Add: Location, features, specific things you like.',
        },
        {
          text: 'Short answer: "I enjoy traveling."',
          example: 'I enjoy traveling whenever I get the chance. Last year, I visited Japan, which was absolutely fascinating. I particularly enjoyed experiencing the culture and trying local food like ramen and sushi.',
          tip: 'Add: How often? Where have you been? What did you enjoy?',
        },
      ],
    },
    {
      title: 'Thinking Aloud Practice',
      type: 'writing',
      instructions: 'Use "thinking aloud" phrases to start your answers naturally.',
      questions: [
        {
          text: 'Question: "What do you think about online education?"',
          example: 'That\'s an interesting question... I think online education has really transformed the way we learn. In my experience, it offers flexibility that traditional classrooms can\'t match.',
          tip: 'Start with "That\'s an interesting question..." to buy thinking time.',
        },
        {
          text: 'Question: "How has technology changed communication?"',
          example: 'Well, that\'s something I\'ve thought about quite a bit, actually. I believe technology has made communication much faster and more accessible, though perhaps less personal.',
          tip: 'Use "Well..." or "That\'s something I\'ve thought about..." to begin.',
        },
      ],
    },
  ],
  tips: [
    'Practice speaking for 2 minutes on any topic',
    'Record yourself and listen for pauses',
    'Think in English, not your first language',
    'Accept imperfection—keep talking!',
  ],
  dailyPractice: [
    'Speak for 1-2 minutes on random topics',
    'Practice with the Liwaza AI Speaking partner',
    'Shadow native speakers (repeat after them)',
    'Record and review your fluency',
  ],
  backPath: '/exams/english/#speaking',
  backLabel: 'Back to Speaking',
  aiContext: 'Fluency Training: Techniques for speaking smoothly without hesitation, including natural fillers, chunking, and extending answers.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...fluencyTrainingData,
      title: fluencyTrainingData.title + ' (Foundations)',
      overview: {
        ...fluencyTrainingData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: fluencyTrainingData.sections.slice(0, Math.max(2, Math.ceil(fluencyTrainingData.sections.length / 2))),
      exercises: fluencyTrainingData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...fluencyTrainingData,
      title: fluencyTrainingData.title + ' (Advanced)',
      overview: {
        ...fluencyTrainingData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: fluencyTrainingData.sections,
      exercises: fluencyTrainingData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(fluencyTrainingData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return fluencyTrainingData;
}
